import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { trajectoryAPI } from '../../services/api';
import { useAgent } from '../../context/AgentContext';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import CohortScatter from './CohortScatter';
import TrajectoryBookList from './TrajectoryBookList';

export default function PlayerTrajectoryPage() {
  const { selectedAgent } = useAgent();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [displayPlayers, setDisplayPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(180);
  const [quadrantFilter, setQuadrantFilter] = useState(null);
  const prevAgentRef = useRef(null);

  useEffect(() => {
    if (!selectedAgent) {
      setDisplayPlayers([]);
      return;
    }
    const agentChanged = prevAgentRef.current !== selectedAgent;
    prevAgentRef.current = selectedAgent;
    if (agentChanged) {
      setDisplayPlayers([]);
    }
    setPlayersLoading(true);
    setError('');
    trajectoryAPI.getPlayers(selectedAgent, days)
      .then((res) => {
        const next = res.data.players || [];
        setDisplayPlayers(next);
      })
      .catch((err) => {
          setDisplayPlayers([]);
        setError(err?.response?.data?.error || 'Failed to load the player book.');
      })
      .finally(() => setPlayersLoading(false));
  }, [selectedAgent, days]);

  // Deep link: /player-trajectory?user_id=<id> → redirect to player profile
  useEffect(() => {
    const raw = searchParams.get('user_id');
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (Number.isInteger(id) && id > 0) {
      navigate(`/player/${id}?tab=trajectory&from=trajectory`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleSelect = (userId) => navigate(`/player/${userId}?tab=trajectory&from=trajectory`);

  return (
    <div className="max-w-[1320px] mx-auto">
      {/* Header */}
      <div className="sticky top-14 z-20 bg-surface pb-3 pt-1 flex items-start justify-between gap-4 border-b border-white/5 mb-6">
        <div>
          <h1 className="text-lg font-bold text-ink">
            Player Trajectory &amp; Bonus Impact
          </h1>
          <p className="text-xs text-ink-dim mt-0.5">
            Whether a VIP player is trending up or down, and how their bonus activity lines up with that trajectory.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className="px-2.5 py-1 rounded text-[11px] font-semibold transition-colors"
              style={{
                background: days === d ? '#d4a843' : '#1f2430',
                color: days === d ? '#13161b' : '#7a8499',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <Alert variant="error" className="mb-4 text-xs">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError('')}
              className="shrink-0 text-negative hover:text-negative h-auto px-1 py-0"
              aria-label="Dismiss error"
            >
              ✕
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Player book */}
      {selectedAgent ? (
        <>
          <div className="mb-6">
            <TrajectoryBookList
              players={displayPlayers}
              loading={playersLoading}
              selectedUserId={null}
              onSelect={handleSelect}
              days={days}
              quadrantFilter={quadrantFilter}
              onQuadrantFilterChange={setQuadrantFilter}
            />
          </div>
          <div className="mb-6">
            <CohortScatter players={displayPlayers} onSelect={handleSelect} quadrantFilter={quadrantFilter} />
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center min-h-[280px] rounded-lg border border-white/10 bg-surface-1 p-6">
          <p className="text-[13px] text-ink-dim">
            Select a VIP manager in the top bar to view their player book.
          </p>
        </div>
      )}
    </div>
  );
}
